import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDog, faCat } from "@fortawesome/pro-solid-svg-icons";
import GoldPreventive from "@/shared/media/images/gold-preventive.svg";
import PlatinumPreventive from "@/shared/media/images/platinum-preventive.svg";

import { Card, CardHeader, CardContent } from "@/shared/components/ui/Card";
import { Policy } from "@/shared/types/Quote.interface";
import { cn } from "@/shared/utils";
import Strings from "../utils/Strings.constants";
import { PublicConfig } from "@/shared/PublicConfig";
import kebabCase from "lodash/kebabCase";

function formatAnnualLimit(value: number | undefined | null): string {
    if (value == null) return "—";
    if (value === PublicConfig.UNLIMITED_ANNUAL_LIMIT_VALUE) return Strings.UNLIMITED_ANNUAL_LIMIT_LABEL;
    return `$${value.toLocaleString()}`;
}

interface PetCardQuickEnrollProps {
    pet: Policy;
    activePetId?: string;
    handleActivePetChange: (petId: string) => void;
    onSelect?: () => void;
    disabled?: boolean;
}

export default function PetCardQuickEnroll(props: PetCardQuickEnrollProps) {
    const { disabled = false, pet, activePetId, handleActivePetChange, onSelect } = props;
    const coverages = pet.coverageSettings?.coverages ?? [];
    const amounts = pet.coverageSettings?.amounts;

    // Plan type label
    const hasAccident = coverages.some(c => c.type === "accident");
    const hasIllness = coverages.some(c => c.type === "illness");
    const planLabel = hasAccident && hasIllness ? "Accident & Illness Plan" : "Accident Only Plan";

    // Formatted amounts
    const formattedAnnualLimit = formatAnnualLimit(amounts?.annualLimit);
    const formattedReimbursementRate = amounts?.reimbursementRate != null ? `${amounts.reimbursementRate}%` : "—";
    const formattedDeductible = amounts?.annualDeductible != null ? `$${amounts.annualDeductible.toLocaleString()}` : "—";
    const hasUnlimited = amounts?.annualLimit === PublicConfig.UNLIMITED_ANNUAL_LIMIT_VALUE;

    // Wellness tier
    const wellnessCoverage = coverages.find(c => c.type === "wellness-gold" || c.type === "wellness-platinum");
    const hasWellness = !!wellnessCoverage;
    const wellnessTierLabel = wellnessCoverage?.type === "wellness-platinum" ? "Platinum" : "Gold";
    const wellnessImage = wellnessCoverage?.type === "wellness-platinum" ? <PlatinumPreventive className="w-10" /> : <GoldPreventive className="w-10" />;

    // Active pet card opacity (matches PetCard pattern)
    const isActivePetCard = !!activePetId && activePetId === pet.id;

    const handleCardClick = () => {
        handleActivePetChange(pet.id ?? "");
        onSelect?.();
    };

    const coverageData = [
        {
            label: Strings.ANNUAL_LIMIT,
            value: formattedAnnualLimit,
            labelClass: cn("mx-1", { "mx-4 sm:mx-1": hasUnlimited }),
            valueClass: cn({ "sm:text-xl": hasUnlimited }),
            colClass: cn("sm:pl-0 border-r border-stroke-secondary sm:max-w-[130px]")
        },
        { label: Strings.REIMBURSEMENT_RATE, value: formattedReimbursementRate, labelClass: "", colClass: "border-r border-stroke-secondary" },
        { label: Strings.ANNUAL_DEDUCTIBLE, value: formattedDeductible, labelClass: "mx-1", colClass: "sm:pr-0" }
    ];

    return (
        <div className="group relative flex size-full">
            <button
                type="button"
                onClick={handleCardClick}
                className="flex size-full flex-col overflow-hidden !rounded-3xl border-0 text-left shadow-md transition-opacity duration-200 ease-in-out focus:shadow-none focus:outline-none"
                disabled={disabled}
                data-testid={`pet-card-quick-enroll-${pet.id}`}
            >
                <Card className={cn("size-full overflow-hidden !rounded-3xl bg-background-primary", !isActivePetCard && "opacity-50 group-hover:opacity-100")}>
                    {/* Header */}
                    <CardHeader className="bg-background-primary-invert p-4 text-content-primary-invert">
                        <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={pet.species === "Cat" ? faCat : faDog} className="size-5 shrink-0" />
                            <p className="flex min-w-0 items-center text-sm font-bold sm:text-base">
                                <span className="truncate">{pet.name}</span>
                                <span className="shrink-0">&rsquo;s</span>
                            </p>
                            <p className="shrink-0 text-sm font-bold sm:text-base">{planLabel}</p>
                        </div>
                    </CardHeader>

                    {/* Body */}
                    <CardContent className="bg-background-primary p-3 pt-4 sm:px-3 sm:py-4">
                        <div className="mx-auto flex flex-col gap-4 sm:max-w-[500px]">
                            <div className="flex items-stretch justify-center">
                                {coverageData.map((data, index) => (
                                    <div key={index} className={cn("flex flex-col items-center justify-between gap-2 pl-2 pr-2 text-center sm:pl-6 sm:pr-6", data.colClass)}>
                                        <p className={cn("text-xs font-bold text-content-primary sm:text-sm", data.labelClass)}>{data.label}</p>
                                        <span
                                            className={cn(
                                                "rounded-lg bg-background-brand-b-secondary p-1 text-lg font-bold text-content-primary xs:px-2 sm:text-2xl",
                                                data.valueClass
                                            )}
                                            data-testid={`pet-card-quick-enroll-${pet.id}-${kebabCase(data.label)}`}
                                        >
                                            {data.value}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {/* Preventive Care row */}
                            {hasWellness && (
                                <div className="mt-4 flex items-center justify-center gap-3 ">
                                    <p className="text-xs font-bold text-content-primary sm:text-sm">Preventive Care:</p>
                                    <div className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-background-secondary p-2">
                                        {wellnessImage}
                                        <p
                                            className="text-lg font-bold text-content-primary"
                                            data-testid={`pet-card-quick-enroll-wellness-${pet.id}-${kebabCase(wellnessTierLabel)}`}
                                        >
                                            {wellnessTierLabel}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </button>
        </div>
    );
}
